import { DeleteIcon } from '@chakra-ui/icons';
import {
    Badge,
    Box,
    Button,
    Flex,
    HStack,
    IconButton,
    Input,
    SimpleGrid,
    Spinner,
    Text,
    Textarea,
    VStack,
    useColorModeValue,
    useToast,
} from '@chakra-ui/react';
import { useCallback, useMemo, useState } from 'react';
import ErrorState from '../../components/states/ErrorState';
import PageCard from '../../components/page-card';
import { translate } from '../../i18n';
import { VideoCommentDto } from '../../models/video-review/dto/video-comment.dto';
import { VideoSubmissionDto, VideoSubmissionStatus } from '../../models/video-review/dto/video-submission.dto';
import { paramFromWindowLocation } from '../../utils/url-parser';
import { useAddVideoComment } from './hooks/useAddVideoComment';
import { useDeleteVideoComment } from './hooks/useDeleteVideoComment';
import { useGetVideoSubmission } from './hooks/useGetVideoSubmission';
import { useUpdateVideoSubmission } from './hooks/useUpdateVideoSubmission';
import { statusColorMap as STATUS_COLOR_MAP } from './components/VideoSubmissionList';

export default function VideoSubmissionDetailPage() {
    const submissionId = paramFromWindowLocation('submission');
    const { data: submission, isLoading, error, refetch } = useGetVideoSubmission(submissionId || '');

    if (isLoading && !submission) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <Spinner size="lg" />
            </Box>
        );
    }

    if (error || !submission) {
        return (
            <ErrorState message={translate('video_review.error_loading')} onRetry={refetch} />
        );
    }

    return (
        <VStack spacing={6} align="stretch">
            <PageCard showBackButton title={translate('video_review.detail_title')}>
                <SubmissionInfo submission={submission} />
            </PageCard>

            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
                <PageCard title={translate('video_review.video')}>
                    <VideoPlayer submission={submission} />
                </PageCard>

                <PageCard title={translate('video_review.comments')}>
                    <CommentsPanel submission={submission} />
                </PageCard>
            </SimpleGrid>
        </VStack>
    );
}

function SubmissionInfo({ submission }: { submission: VideoSubmissionDto }) {
    const textColor = useColorModeValue('secondaryGray.900', 'white');
    const labelColor = useColorModeValue('gray.500', 'gray.400');
    const updateMutation = useUpdateVideoSubmission();
    const toast = useToast();

    const handleStatusChange = async (newStatus: VideoSubmissionStatus) => {
        try {
            await updateMutation.mutateAsync({
                id: submission.id,
                input: { status: newStatus },
            });
            toast({
                title: translate('video_review.status_updated'),
                status: 'success',
                duration: 2000,
            });
        } catch (error: any) {
            toast({
                title: translate('video_review.error_updating'),
                description: error.message,
                status: 'error',
                duration: 5000,
            });
        }
    };

    const playerName = `${submission.uploadedBy.firstName || ''} ${submission.uploadedBy.lastName || ''}`.trim() || translate('common.unknown');

    return (
        <Box px="25px" pb="20px">
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <Box>
                    <Text color={labelColor} fontSize="sm">{translate('video_review.player')}</Text>
                    <Text color={textColor} fontWeight="600">{playerName}</Text>
                </Box>
                <Box>
                    <Text color={labelColor} fontSize="sm">{translate('video_review.title')}</Text>
                    <Text color={textColor} fontWeight="600">{submission.title}</Text>
                </Box>
                {submission.description && (
                    <Box gridColumn={{ md: 'span 2' }}>
                        <Text color={labelColor} fontSize="sm">{translate('video_review.description')}</Text>
                        <Text color={textColor}>{submission.description}</Text>
                    </Box>
                )}
                <Box>
                    <Text color={labelColor} fontSize="sm">{translate('video_review.sport')}</Text>
                    <Text color={textColor}>{submission.sport || '-'}</Text>
                </Box>
                <Box>
                    <Text color={labelColor} fontSize="sm">{translate('video_review.status')}</Text>
                    <Badge colorScheme={STATUS_COLOR_MAP[submission.status]}>
                        {translate(`video_review.${submission.status}`)}
                    </Badge>
                </Box>
                <Box>
                    <Text color={labelColor} fontSize="sm">{translate('video_review.assigned_to')}</Text>
                    <Text color={textColor}>{submission.assignedTo?.email || '-'}</Text>
                </Box>
                <Box>
                    <Text color={labelColor} fontSize="sm">{translate('video_review.date')}</Text>
                    <Text color={textColor}>{new Date(submission.createdAt).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</Text>
                </Box>
            </SimpleGrid>

            <HStack mt={4} spacing={3}>
                {submission.status === VideoSubmissionStatus.PENDING && (
                    <Button
                        size="sm"
                        colorScheme="brand"
                        isLoading={updateMutation.isLoading}
                        onClick={() => handleStatusChange(VideoSubmissionStatus.IN_REVIEW)}
                    >
                        {translate('video_review.start_review')}
                    </Button>
                )}
                {submission.status === VideoSubmissionStatus.IN_REVIEW && (
                    <Button
                        size="sm"
                        colorScheme="green"
                        isLoading={updateMutation.isLoading}
                        onClick={() => handleStatusChange(VideoSubmissionStatus.REVIEWED)}
                    >
                        {translate('video_review.mark_reviewed')}
                    </Button>
                )}
            </HStack>
        </Box>
    );
}

function VideoPlayer({ submission }: { submission: VideoSubmissionDto }) {
    return (
        <Box px="25px" pb="20px">
            <video
                id="video-player"
                controls
                style={{ width: '100%', maxHeight: '500px', borderRadius: '8px' }}
                src={submission.videoUrl}
            >
                {translate('video_review.video_not_supported')}
            </video>
        </Box>
    );
}

function formatTimestamp(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function CommentsPanel({ submission }: { submission: VideoSubmissionDto }) {
    const textColor = useColorModeValue('secondaryGray.900', 'white');
    const labelColor = useColorModeValue('gray.500', 'gray.400');
    const bgColor = useColorModeValue('gray.50', 'whiteAlpha.100');
    const borderTopColor = useColorModeValue('gray.200', 'whiteAlpha.200');
    const toast = useToast();

    const addCommentMutation = useAddVideoComment();
    const deleteCommentMutation = useDeleteVideoComment();

    const [commentText, setCommentText] = useState('');
    const [timestampInput, setTimestampInput] = useState('');

    const comments = useMemo(() => [...(submission.comments || [])].sort((a, b) => {
        if (a.timestampSeconds != null && b.timestampSeconds != null) {
            return a.timestampSeconds - b.timestampSeconds;
        }
        if (a.timestampSeconds != null) return -1;
        if (b.timestampSeconds != null) return 1;
        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    }), [submission.comments]);

    const captureCurrentTime = useCallback(() => {
        const video = document.getElementById('video-player') as HTMLVideoElement;
        if (video) {
            const seconds = Math.floor(video.currentTime);
            setTimestampInput(seconds.toString());
        }
    }, []);

    const seekToTime = useCallback((seconds: number) => {
        const video = document.getElementById('video-player') as HTMLVideoElement;
        if (video) {
            video.currentTime = seconds;
            video.play();
        }
    }, []);

    const handleAddComment = async () => {
        if (!commentText.trim()) return;

        try {
            await addCommentMutation.mutateAsync({
                submissionId: submission.id,
                input: {
                    content: commentText.trim(),
                    timestampSeconds: timestampInput ? parseFloat(timestampInput) : undefined,
                },
            });
            setCommentText('');
            setTimestampInput('');
        } catch (error: any) {
            toast({
                title: translate('video_review.error_adding_comment'),
                description: error.message,
                status: 'error',
                duration: 5000,
            });
        }
    };

    const handleDeleteComment = async (comment: VideoCommentDto) => {
        try {
            await deleteCommentMutation.mutateAsync({
                submissionId: submission.id,
                commentId: comment.id,
            });
        } catch (error: any) {
            toast({
                title: translate('video_review.error_deleting_comment'),
                description: error.message,
                status: 'error',
                duration: 5000,
            });
        }
    };

    return (
        <Box px="25px" pb="20px">
            <VStack spacing={3} align="stretch" maxH="400px" overflowY="auto" mb={4}>
                {comments.length === 0 && (
                    <Text color={labelColor} fontSize="sm" textAlign="center" py={4}>
                        {translate('video_review.no_comments')}
                    </Text>
                )}
                {comments.map((comment) => (
                    <Box key={comment.id} bg={bgColor} p={3} borderRadius="md">
                        <Flex justify="space-between" align="start">
                            <Box flex={1}>
                                <HStack spacing={2} mb={1}>
                                    <Text fontSize="xs" fontWeight="600" color={textColor}>
                                        {comment.author.email}
                                    </Text>
                                    {comment.timestampSeconds != null && (
                                        <Badge
                                            colorScheme="purple"
                                            cursor="pointer"
                                            onClick={() => seekToTime(comment.timestampSeconds!)}
                                            _hover={{ opacity: 0.8 }}
                                        >
                                            {formatTimestamp(comment.timestampSeconds)}
                                        </Badge>
                                    )}
                                    <Text fontSize="xs" color={labelColor}>
                                        {new Date(comment.createdAt).toLocaleDateString(undefined, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                                    </Text>
                                </HStack>
                                <Text fontSize="sm" color={textColor}>
                                    {comment.content}
                                </Text>
                            </Box>
                            <IconButton
                                aria-label={translate('video_review.delete_comment')}
                                icon={<DeleteIcon />}
                                size="xs"
                                variant="ghost"
                                colorScheme="red"
                                onClick={() => handleDeleteComment(comment)}
                            />
                        </Flex>
                    </Box>
                ))}
            </VStack>

            <Box borderTop="1px" borderColor={borderTopColor} pt={3}>
                <Textarea
                    placeholder={translate('video_review.comment_placeholder')}
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    size="sm"
                    rows={2}
                    mb={2}
                />
                <Flex gap={2} align="center">
                    <HStack spacing={2} flex={1}>
                        <Input
                            placeholder={translate('video_review.timestamp_seconds')}
                            value={timestampInput}
                            onChange={(e) => setTimestampInput(e.target.value)}
                            size="sm"
                            w="120px"
                            type="number"
                        />
                        <Button size="xs" variant="outline" onClick={captureCurrentTime}>
                            {translate('video_review.capture_time')}
                        </Button>
                    </HStack>
                    <Button
                        size="sm"
                        colorScheme="brand"
                        onClick={handleAddComment}
                        isLoading={addCommentMutation.isLoading}
                        isDisabled={!commentText.trim()}
                    >
                        {translate('video_review.add_comment')}
                    </Button>
                </Flex>
            </Box>
        </Box>
    );
}
